/**
 * useThreadCategories — the viewer's sections, live (chat-thread experience
 * PR-5, 2026-09-09; built-ins first-class 2026-09-09). Subscribes per
 * signed-in uid and exposes the whole organization surface over the one meta
 * doc: create (folding by name), rename (built-ins too), remove (custom
 * only), move up / down, collapse. Optimistic: the list updates locally at
 * once and the write settles behind it; a failed write is undone by the next
 * snapshot. A stored custom category named like a built-in is folded into it
 * on read (the cleaned list is written back once; the host re-files threads
 * through `foldAliases`).
 *
 * ORDER (Miles, 2026-09-09): the built-ins render in the platform default —
 * group threads → threads → direct messages, below the custom categories —
 * until the viewer REORDERS (`builtinOrderTouched`, stamped only by
 * `moveCategory`); from then on their stored order is the order. A move can
 * be relative to the VISIBLE sections (`among`): the rail hides empty
 * sections, and a swap with a hidden neighbour used to show no change at all.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  saveThreadCategories,
  subscribeThreadCategories,
  type ThreadCategoriesState,
} from '../repositories/threadCategoriesRepository';
import {
  BUILTIN_SECTION_IDS,
  type BuiltinSectionKind,
  MAX_THREAD_CATEGORIES,
  adoptBuiltinSection,
  builtinKindForName,
  builtinKindOf,
  builtinsNeedingAdoption,
  clearAdoptionStamp,
  customCategories,
  findCategoryByName,
  isBuiltinCategoryId,
  newThreadCategoryId,
  normalizeThreadCategoryName,
  orderBuiltinsByDefault,
  withBuiltinSections,
  type ThreadCategory,
} from '../types/threadCategories';
import { resolveThreadSectionAdoptEnabled } from '../services/threadAddPeople/featureFlags';

export type CategoryMoveDirection = 'up' | 'down';

export interface CategoryMoveOptions {
  /**
   * The ids of the sections the viewer can SEE, in display order. When given,
   * the move is relative to the visible neighbour: the section lands just
   * before / after it in the full list, crossing any hidden (empty) sections
   * in between — so every move is visible at once. Without it the move swaps
   * with the adjacent section of the full list (the sections manager, where
   * every section is listed).
   */
  among?: readonly string[];
}

/**
 * The list as displayed: the viewer's own order once they have reordered, else
 * the platform default.
 *
 * `include` names the CONDITIONAL built-ins this viewer gets — "triage
 * reports" materializes only for the people who actually have report threads
 * (2026-09-16), so nobody else finds an unfillable section in their list.
 */
export function displayedCategories(
  state: Pick<ThreadCategoriesState, 'items' | 'builtinOrderTouched'>,
  include?: readonly BuiltinSectionKind[],
): ThreadCategory[] {
  const full = withBuiltinSections(state.items, { include });
  return state.builtinOrderTouched ? full : orderBuiltinsByDefault(full, { include });
}

export interface UseThreadCategoriesResult {
  /** Every section in the viewer's order — custom categories and the three built-ins. */
  categories: ThreadCategory[];
  collapsed: ReadonlySet<string>;
  /** Custom ids that fold into a built-in (threads filed under them read as filed under the built-in). */
  foldAliases: Readonly<Record<string, string>>;
  /** Create a category by name — or return the id of the section that name already means (fold). Null = empty name / cap hit. */
  createCategory: (name: string) => Promise<string | null>;
  /** Rename any section (a built-in renamed back to its default label stores no name). False = empty or taken. */
  /**
   * Rename a section. A name another section already wears is NOT refused
   * (2026-09-16): it comes back as `{reason:'clash', clash}` so the surface can
   * ask "move everything there?" and call again with `{mergeInto: clash.id}`.
   */
  renameCategory: (id: string, name: string, opts?: RenameCategoryOptions) => Promise<RenameCategoryResult>;
  /** Remove a CUSTOM category (a built-in is refused). The host re-files its threads. */
  removeCategory: (id: string) => Promise<boolean>;
  moveCategory: (id: string, direction: CategoryMoveDirection, opts?: CategoryMoveOptions) => Promise<boolean>;
  setCollapsed: (id: string, collapsed: boolean) => Promise<void>;
  /**
   * Section moves the host still owes a batched re-file (2026-09-13). ONE rule
   * covers both: every thread DISPLAYED in `fromId` moves to `toId`, then
   * `clearAdoption(clearId)`.
   *
   * Named by role rather than by kind on purpose — an adoption and a merge are
   * the same pair of sections with the threads going opposite ways, and a host
   * that had to tell them apart would eventually get one backwards.
   */
  pendingAdoptions: ReadonlyArray<{ fromId: string; toId: string; clearId: string }>;
  clearAdoption: (clearId: string) => Promise<void>;
}

/**
 * Why a rename comes back with a REASON rather than `false` (2026-09-16, Miles:
 * "I tried to change a category name and it didn't allow me to press enter
 * because a category already had that name. it should allow me to and confirm
 * with the user really quick … and then when user confirms real quick moves all
 * threads in that category under the category that already exists").
 *
 * A flat refusal cannot say WHICH section took the name, so the surface could
 * only show "a section already has that name" and leave the person to work out
 * the rest themselves. A clash carries the section it collided with; the
 * surface confirms and calls again with `mergeInto`.
 */
export type RenameCategoryResult =
  | { ok: true }
  | { ok: false; reason: 'empty' | 'missing' | 'capped' }
  // The category itself, not a label: names are resolved at render, in the
  // viewer's language (`categoryLabel`), and this hook stays language-free.
  | { ok: false; reason: 'clash'; clash: ThreadCategory };

export interface RenameCategoryOptions {
  /**
   * The section this one is being merged INTO, answered by the person after a
   * clash. Every thread displayed in the renamed section moves there; the
   * renamed section then disappears if it is the viewer's own, or goes back to
   * its default label if it is a default section (a default is never deleted).
   */
  mergeInto?: string;
}

const EMPTY_STATE: ThreadCategoriesState = { items: [], collapsed: [], foldAliases: {}, builtinOrderTouched: false };

export interface UseThreadCategoriesOptions {
  /**
   * Conditional built-ins this viewer should get (CONDITIONAL_BUILTIN_SECTIONS).
   * The host derives it from the threads the viewer actually holds.
   */
  includeBuiltins?: readonly BuiltinSectionKind[];
}

export function useThreadCategories(
  uid: string | null | undefined,
  opts?: UseThreadCategoriesOptions,
): UseThreadCategoriesResult {
  const [state, setState] = useState<ThreadCategoriesState>(EMPTY_STATE);
  const latestRef = useRef<ThreadCategoriesState>(EMPTY_STATE);
  latestRef.current = state;
  const foldWrittenRef = useRef(false);
  const adoptWrittenRef = useRef(false);
  const adoptEnabled = useMemo(() => resolveThreadSectionAdoptEnabled(), []);

  useEffect(() => {
    const id = (uid ?? '').trim();
    setState(EMPTY_STATE);
    foldWrittenRef.current = false;
    adoptWrittenRef.current = false;
    if (!id) return undefined;
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = subscribeThreadCategories(id, (next) => {
        setState(next);
        // A DEFAULT section still wearing somebody's name owes an adoption
        // (2026-09-13). This is the reported bug's own state — renaming "group
        // threads" to "triage" used to set a label and nothing else — so the
        // fix has to reach a list renamed BEFORE it shipped, not only the next
        // rename. Migrated on read, once, in the same write-back shape the
        // fold alias already uses; the host re-files through `pendingAdoptions`.
        if (adoptEnabled && !adoptWrittenRef.current) {
          const owed = builtinsNeedingAdoption(next.items);
          if (owed.length > 0) {
            adoptWrittenRef.current = true;
            let migrated = withBuiltinSections(next.items);
            for (const section of owed) {
              const result = adoptBuiltinSection(migrated, section.id, section.name);
              // A name the cap or a clash refuses keeps today's behaviour for
              // that one section rather than failing the whole migration.
              if (result) migrated = result.items;
            }
            setState((prev) => ({ ...prev, items: migrated }));
            saveThreadCategories(id, migrated, next.collapsed).catch(() => undefined);
            return;
          }
        }
        // A folded custom section is gone from the list it came from: write
        // the cleaned list back once so every device agrees.
        if (Object.keys(next.foldAliases).length > 0 && !foldWrittenRef.current) {
          foldWrittenRef.current = true;
          saveThreadCategories(id, withBuiltinSections(next.items), next.collapsed).catch(() => undefined);
        }
      });
    } catch {
      unsubscribe = null;
    }
    return () => { unsubscribe?.(); };
  }, [uid, adoptEnabled]);

  const includeKey = (opts?.includeBuiltins ?? []).join(',');
  const includeRef = useRef<readonly BuiltinSectionKind[]>(opts?.includeBuiltins ?? []);
  includeRef.current = opts?.includeBuiltins ?? [];
  const categories = useMemo(
    () => displayedCategories(
      { items: state.items, builtinOrderTouched: state.builtinOrderTouched },
      includeKey ? (includeKey.split(',') as BuiltinSectionKind[]) : [],
    ),
    [state.items, state.builtinOrderTouched, includeKey],
  );
  const collapsed = useMemo(() => new Set(state.collapsed), [state.collapsed]);
  /**
   * Merges awaiting the host's re-file. Held in memory rather than on the
   * section, because a merge DELETES the section — there is nowhere to stamp.
   */
  const [merges, setMerges] = useState<Array<{ fromId: string; toId: string; clearId: string }>>([]);

  const persist = useCallback(async (items: ThreadCategory[], collapsed?: string[], opts?: { builtinOrderTouched?: boolean }) => {
    const id = (uid ?? '').trim();
    const full = withBuiltinSections(items, { include: includeRef.current });
    setState((prev) => ({
      ...prev,
      items: full,
      ...(collapsed ? { collapsed } : {}),
      ...(opts?.builtinOrderTouched ? { builtinOrderTouched: true } : {}),
    }));
    if (!id) return;
    try {
      await saveThreadCategories(id, full, collapsed ?? latestRef.current.collapsed, opts);
    } catch { /* the next snapshot restores the stored list */ }
  }, [uid]);

  const createCategory = useCallback(async (rawName: string): Promise<string | null> => {
    const name = normalizeThreadCategoryName(rawName);
    if (!name) return null;
    const current = displayedCategories(latestRef.current, includeRef.current);
    const existing = findCategoryByName(current, name);
    if (existing) return existing.id;
    if (customCategories(current).length >= MAX_THREAD_CATEGORIES) return null;
    const id = newThreadCategoryId();
    // A new category joins ABOVE the built-ins (custom first — today's rail).
    const firstBuiltin = current.findIndex((c) => !!c.builtin);
    const at = firstBuiltin < 0 ? current.length : firstBuiltin;
    const next = [...current.slice(0, at), { id, name, order: at }, ...current.slice(at)];
    await persist(next);
    return id;
  }, [persist]);

  const renameCategory = useCallback(async (
    id: string,
    rawName: string,
    opts?: RenameCategoryOptions,
  ): Promise<RenameCategoryResult> => {
    const name = normalizeThreadCategoryName(rawName);
    if (!name) return { ok: false, reason: 'empty' };
    const current = displayedCategories(latestRef.current, includeRef.current);
    const target = current.find((c) => c.id === id);
    if (!target) return { ok: false, reason: 'missing' };
    const targetKind = builtinKindOf(target.id);

    // MERGE, answered (2026-09-16). One rule, whatever the two sections are:
    // every thread displayed in this one moves to that one, then this one goes
    // — deleted if it is the viewer's own, back to its default label if it is a
    // default section, which is never deleted. The threads live on the viewer's
    // index docs, which this list cannot reach, so the host does that batched
    // write through the same `pendingAdoptions` channel an adoption uses.
    const mergeInto = (opts?.mergeInto ?? '').trim();
    if (mergeInto && mergeInto !== id && current.some((c) => c.id === mergeInto)) {
      setMerges((prev) => [
        ...prev.filter((m) => m.clearId !== id),
        { fromId: id, toId: mergeInto, clearId: id },
      ]);
      await persist(targetKind
        ? current.map((c) => (c.id === id ? { ...c, name: '' } : c))
        : current.filter((c) => c.id !== id));
      return { ok: true };
    }

    // A built-in renamed to its own default label keeps no name (the label
    // follows the language) — and is a relabel, never an adoption.
    if (targetKind && builtinKindForName(name) === targetKind) {
      await persist(current.map((c) => (c.id === id ? { ...c, name: '' } : c)));
      return { ok: true };
    }

    // MERGE, the inverse of an adoption (2026-09-13): renaming YOUR section to
    // a default section's name folds it into that section, closing the gap
    // PR-B's fold rule left — it made CREATING "direct messages" fold, while
    // renaming to it was refused as a taken name. The host re-files its
    // threads under the built-in through the same `pendingAdoptions` channel,
    // read the other way round.
    const foldsInto = !targetKind ? builtinKindForName(name) : null;
    if (foldsInto) {
      setMerges((prev) => [
        ...prev.filter((m) => m.clearId !== id),
        { fromId: id, toId: BUILTIN_SECTION_IDS[foldsInto], clearId: id },
      ]);
      await persist(current.filter((c) => c.id !== id));
      return { ok: true };
    }

    // A name another section wears is a QUESTION, not a refusal — the surface
    // asks whether to merge and calls back with `mergeInto`.
    const clash = findCategoryByName(current, name);
    if (clash && clash.id !== id) {
      return { ok: false, reason: 'clash', clash };
    }

    // ADOPTION: renaming a DEFAULT section makes it the viewer's own and it
    // keeps its threads, while the built-in returns to its default label for
    // new threads of that kind (Miles, 2026-09-12).
    if (targetKind && adoptEnabled) {
      const adopted = adoptBuiltinSection(current, id, name);
      if (!adopted) return { ok: false, reason: 'capped' };
      await persist(adopted.items);
      return { ok: true };
    }

    await persist(current.map((c) => (c.id === id ? { ...c, name } : c)));
    return { ok: true };
  }, [persist, adoptEnabled]);

  const removeCategory = useCallback(async (id: string): Promise<boolean> => {
    if (isBuiltinCategoryId(id)) return false;
    const current = displayedCategories(latestRef.current, includeRef.current);
    if (!current.some((c) => c.id === id)) return false;
    await persist(current.filter((c) => c.id !== id));
    return true;
  }, [persist]);

  const moveCategory = useCallback(async (id: string, direction: CategoryMoveDirection, opts?: CategoryMoveOptions): Promise<boolean> => {
    const current = displayedCategories(latestRef.current, includeRef.current);
    const index = current.findIndex((c) => c.id === id);
    if (index < 0) return false;
    const next = [...current];
    const visible = (opts?.among ?? []).filter((v) => current.some((c) => c.id === v));
    const at = visible.indexOf(id);
    if (at >= 0) {
      // Relative to the VISIBLE neighbour: land just before / after it in the
      // full list, crossing the hidden sections in between.
      const neighbourId = visible[direction === 'up' ? at - 1 : at + 1];
      if (!neighbourId) return false;
      const target = current.findIndex((c) => c.id === neighbourId);
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
    } else {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= current.length) return false;
      [next[index], next[target]] = [next[target], next[index]];
    }
    // The stored order becomes the viewer's own from here on.
    await persist(next, undefined, { builtinOrderTouched: true });
    return true;
  }, [persist]);

  /**
   * Sections whose threads the host still has to move: an ADOPTION (stamped
   * durably on the section itself, so a reload mid-move finishes the job) and
   * a MERGE (transient — the section is gone from the list, so there is
   * nothing left to stamp; a reload simply leaves those threads where they
   * were, which is the safe direction).
   */
  const pendingAdoptions = useMemo(() => {
    const fromStamps = categories
      .filter((c) => !!c.adoptedFrom)
      // An adoption moves threads OUT of the built-in and into the section the
      // viewer just named; a merge below moves them the other way.
      .map((c) => ({ fromId: BUILTIN_SECTION_IDS[c.adoptedFrom!], toId: c.id, clearId: c.id }));
    return [...fromStamps, ...merges];
  }, [categories, merges]);

  const clearAdoption = useCallback(async (clearId: string): Promise<void> => {
    setMerges((prev) => (prev.some((m) => m.clearId === clearId) ? prev.filter((m) => m.clearId !== clearId) : prev));
    const current = displayedCategories(latestRef.current, includeRef.current);
    if (!current.some((c) => c.id === clearId && c.adoptedFrom)) return;
    await persist(clearAdoptionStamp(current, clearId));
  }, [persist]);

  const setCollapsed = useCallback(async (id: string, collapsed: boolean): Promise<void> => {
    const current = latestRef.current.collapsed.filter((c) => c !== id);
    const next = collapsed ? [...current, id] : current;
    await persist(displayedCategories(latestRef.current), next);
  }, [persist]);

  return {
    categories,
    collapsed,
    foldAliases: state.foldAliases,
    createCategory,
    renameCategory,
    removeCategory,
    moveCategory,
    setCollapsed,
    pendingAdoptions,
    clearAdoption,
  };
}
